import { useState } from 'react';
import type { WebFrequency } from '@/mocks/superAdminFrequencies';
import { saAvailableRadiosForAssignment } from '@/mocks/superAdminFrequencies';

interface ReserveFrequencyModalProps {
  frequency: WebFrequency | null;
  onClose: () => void;
  onConfirm: (frequency: WebFrequency, reservedFor: string | null) => void;
}

export default function ReserveFrequencyModal({ frequency, onClose, onConfirm }: ReserveFrequencyModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  const [reserveForSelf, setReserveForSelf] = useState(false);

  if (!frequency) return null;

  const filtered = saAvailableRadiosForAssignment.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        <div className="p-5 border-b border-background-200">
          <h3 className="text-foreground-950 font-bold text-sm">Reservar frecuencia</h3>
          <p className="text-foreground-400 text-xs mt-0.5">
            Apartá <span className="font-mono text-foreground-600">{frequency.frequency}</span> para uso futuro
          </p>
        </div>

        <div className="p-5 space-y-4">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-background-200 cursor-pointer hover:bg-background-50 transition-colors">
            <input
              type="checkbox"
              checked={reserveForSelf}
              onChange={(e) => {
                setReserveForSelf(e.target.checked);
                if (e.target.checked) setSelected(null);
              }}
              className="w-4 h-4 rounded accent-foreground-950"
            />
            <span className="text-xs text-foreground-700">Reserva administrativa (sin radio específica)</span>
          </label>

          {!reserveForSelf && (
            <>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar radio para reservar..."
                  className="w-full pl-8 pr-3 py-2.5 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>

              <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-background-200 p-1.5">
                {filtered.length === 0 ? (
                  <p className="text-foreground-400 text-xs text-center py-4">No se encontraron radios</p>
                ) : (
                  filtered.map((radio) => (
                    <button
                      key={radio.id}
                      onClick={() => setSelected(radio)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs transition-colors cursor-pointer ${
                        selected?.id === radio.id
                          ? 'bg-foreground-950 text-background-50'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center shrink-0">
                        <i className="ri-radio-line text-foreground-500 text-xs"></i>
                      </div>
                      {radio.name}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 p-5 border-t border-background-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(frequency, reserveForSelf ? 'Reserva administrativa' : selected?.name ?? null)}
            disabled={!reserveForSelf && !selected}
            className={`flex-1 px-4 py-2.5 rounded-xl text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              reserveForSelf || selected
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-background-200 text-foreground-300 cursor-not-allowed'
            }`}
          >
            Reservar
          </button>
        </div>
      </div>
    </div>
  );
}